import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useRef, useCallback, useEffect } from 'react';
import '../../utils/assertion.esm.js';
import '../../utils/ssr.esm.js';
import '../../utils/number.esm.js';
import '../../utils/array.esm.js';
import '../../utils/breakpoint.esm.js';
import '../../utils/context.esm.js';
import '../../utils/dom.esm.js';
import '../../utils/tabbable.esm.js';
import '../../utils/dom-query.esm.js';
import '../../utils/logger.esm.js';
import '../../utils/focus.esm.js';
import { callAllHandlers } from '../../utils/function.esm.js';
import '../../utils/keydown.esm.js';
import '../../utils/lazy.esm.js';
import '../../utils/object.esm.js';
import { mergeRefs } from '../../utils/react-helpers.esm.js';
import '../../utils/style-props.esm.js';
import '../../utils/tailwind.esm.js';
import '../../hooks/use-safe-layout-effects.esm.js';
import '../../hooks/use-callback-ref.esm.js';
import '../../hooks/use-clickable.esm.js';
import '../../hooks/use-clipboard.esm.js';
import '../../hooks/use-combobox-dropdown-style.esm.js';
import '../../hooks/use-combobox-style.esm.js';
import '../../hooks/use-controllable.esm.js';
import '../../hooks/use-local-storage.esm.js';
import '../../hooks/use-media.esm.js';
import '../../hooks/use-prefers-dark-mode.esm.js';
import '../../hooks/use-dark-mode.esm.js';
import '../../hooks/use-force-update.esm.js';
import '../../hooks/use-descendant.esm.js';
import { useId } from '../../hooks/use-id.esm.js';
import '../../hooks/use-previous.esm.js';
import { useDisclosure } from '../../hooks/use-disclosure.esm.js';
import '../../hooks/use-dropdown-style.esm.js';
import '../../hooks/use-latest-ref.esm.js';
import { useEventListener } from '../../hooks/use-event-listener.esm.js';
import '../../hooks/use-event-listener-v2.esm.js';
import '../../hooks/use-update-effect.esm.js';
import '../../hooks/use-focus-on-hide.esm.js';
import '../../hooks/use-pointer-event.esm.js';
import '../../hooks/use-focus-on-pointerdown.esm.js';
import '../../hooks/use-focus-on-show.esm.js';
import '../../hooks/use-fork-ref.esm.js';
import '../../hooks/use-hints.esm.js';
import '../../hooks/use-input-style.esm.js';
import '../../hooks/use-key-press.esm.js';
import '../../hooks/use-lock-body-scroll.esm.js';
import '../../hooks/use-platform.esm.js';
import '../../hooks/use-shortcut.esm.js';
import '../Popper/popper.modifiers.esm.js';
import { usePopper } from '../Popper/use-popper.esm.js';
import '../Popper/use-popper-v2.esm.js';

function useTooltip(props) {
  if (props === void 0) {
    props = {};
  }

  var _props = props,
      _props$openDelay = _props.openDelay,
      openDelay = _props$openDelay === void 0 ? 0 : _props$openDelay,
      _props$closeDelay = _props.closeDelay,
      closeDelay = _props$closeDelay === void 0 ? 0 : _props$closeDelay,
      _props$closeOnClick = _props.closeOnClick,
      closeOnClick = _props$closeOnClick === void 0 ? true : _props$closeOnClick,
      closeOnMouseDown = _props.closeOnMouseDown,
      onOpen = _props.onOpen,
      onClose = _props.onClose,
      placement = _props.placement,
      id = _props.id,
      openProp = _props.open,
      defaultOpen = _props.defaultOpen,
      _props$arrowSize = _props.arrowSize,
      arrowSize = _props$arrowSize === void 0 ? 10 : _props$arrowSize,
      modifiers = _props.modifiers,
      hoverable = _props.hoverable,
      _props$hoverDelay = _props.hoverDelay,
      hoverDelay = _props$hoverDelay === void 0 ? 200 : _props$hoverDelay,
      disabled = _props.disabled;

  var _useDisclosure = useDisclosure({
    open: openProp,
    defaultOpen: defaultOpen,
    onOpen: onOpen,
    onClose: onClose
  }),
      open = _useDisclosure.open,
      onOpenProp = _useDisclosure.onOpen,
      onCloseProp = _useDisclosure.onClose;

  var popper = usePopper({
    forceUpdate: open,
    placement: placement,
    arrowSize: arrowSize,
    modifiers: modifiers
  });
  var tooltipId = useId(id, 'tooltip');
  var ref = useRef(null);
  var triggerRef = mergeRefs(ref, popper.reference.ref);
  var enterTimeout = useRef();
  var exitTimeout = useRef();
  var delayClose = useRef(false);
  var innerCloseDelay = closeDelay < hoverDelay && hoverable ? hoverDelay : closeDelay;
  var openWithDelay = useCallback(function () {
    if (!disabled) {
      enterTimeout.current = window.setTimeout(onOpenProp, openDelay);
    }
  }, [disabled, onOpenProp, openDelay]);
  var closeWithDelay = useCallback(function () {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current);
    }

    exitTimeout.current = window.setTimeout(function () {
      if (!delayClose.current) {
        onCloseProp();
      }
    }, innerCloseDelay);
  }, [innerCloseDelay, onCloseProp]);
  var onClick = useCallback(function () {
    if (closeOnClick) {
      closeWithDelay();
    }
  }, [closeOnClick, closeWithDelay]);
  var onMouseDown = useCallback(function () {
    if (closeOnMouseDown) {
      closeWithDelay();
    }
  }, [closeOnMouseDown, closeWithDelay]);

  var onKeyDown = function onKeyDown(event) {
    if (open && event.key === 'Escape') {
      closeWithDelay();
    }
  };

  var onTooltipMouseEnter = useCallback(function () {
    if (hoverable) {
      delayClose.current = true;
    }
  }, [hoverable, openWithDelay]);
  var onTooltipMouseLeave = useCallback(function () {
    if (hoverable) {
      delayClose.current = false;
      closeWithDelay();
    }
  }, [hoverable, closeWithDelay]);
  useEventListener('keydown', onKeyDown);
  useEffect(function () {
    return function () {
      clearTimeout(enterTimeout.current);
      clearTimeout(exitTimeout.current);
    };
  }, []);
  var getTriggerProps = useCallback(function (triggerProps, externalTriggerRef) {
    if (triggerProps === void 0) {
      triggerProps = {};
    }

    if (externalTriggerRef === void 0) {
      externalTriggerRef = null;
    }

    return _extends({}, triggerProps, {
      ref: mergeRefs(externalTriggerRef, triggerRef),
      onMouseLeave: callAllHandlers(triggerProps.onMouseLeave, closeWithDelay),
      onMouseEnter: callAllHandlers(triggerProps.onMouseEnter, openWithDelay),
      onClick: callAllHandlers(triggerProps.onClick, onClick),
      onMouseDown: callAllHandlers(triggerProps.onMouseDown, onMouseDown),
      onFocus: callAllHandlers(triggerProps.onFocus, openWithDelay),
      onBlur: callAllHandlers(triggerProps.onBlur, closeWithDelay),
      'aria-describedby': open ? tooltipId : undefined
    });
  }, [closeWithDelay, open, onClick, onMouseDown, openWithDelay, tooltipId, triggerRef]);
  var getTooltipProps = useCallback(function (tooltipProps, tooltipRef) {
    if (tooltipProps === void 0) {
      tooltipProps = {};
    }

    if (tooltipRef === void 0) {
      tooltipRef = null;
    }

    return _extends({}, {
      maxWidth: 'max-w-xs',
      padding: ['px-3', 'py-2'],
      backgroundColor: 'bg-gray-900',
      borderRadius: 'rounded-md',
      textColor: 'text-white',
      fontSize: 'text-sm',
      boxShadow: 'shadow-md',
      zIndex: 'z-50'
    }, tooltipProps, {
      onMouseEnter: callAllHandlers(tooltipProps.onMouseEnter, onTooltipMouseEnter),
      onMouseLeave: callAllHandlers(tooltipProps.onMouseLeave, onTooltipMouseLeave),
      id: tooltipId,
      role: 'tooltip',
      ref: mergeRefs(tooltipRef, popper.popper.ref),
      style: _extends({}, tooltipProps.style, popper.popper.style)
    });
  }, [popper.popper.ref, popper.popper.style, tooltipId]);
  var getArrowProps = useCallback(function (arrowProps, arrowRef) {
    if (arrowProps === void 0) {
      arrowProps = {};
    }

    if (arrowRef === void 0) {
      arrowRef = null;
    }

    return _extends({}, {
      backgroundColor: 'bg-inherit'
    }, arrowProps, {
      ref: mergeRefs(arrowRef, popper.arrow.ref),
      style: _extends({}, arrowProps.style, popper.arrow.style)
    });
  }, [popper.arrow.ref, popper.arrow.style]);
  return {
    open: open,
    show: openWithDelay,
    hide: closeWithDelay,
    placement: popper.placement,
    getTriggerProps: getTriggerProps,
    getTooltipProps: getTooltipProps,
    getArrowProps: getArrowProps,
    update: popper.update
  };
}

export { useTooltip };
//# sourceMappingURL=use-tooltip.esm.js.map
